import { Lineicons } from '@lineiconshq/react-lineicons';
import { ChevronDown, LayoutGrid } from 'lucide-react';
import { CheckStroke } from '@lineiconshq/free-icons';
import React, { useMemo } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TABLE_THEME } from '@/components/custom/erp/table-theme';
import type { ErpDataTableTabProps, TableData } from './types';
import { Button } from '@/components/ui/button';
import { useResponsiveLayout } from '@/hooks';
import { cn } from '@/lib/utils';

interface CompactTableTabsProps {
  tables: TableData[];
  activeTab: number;
  onChange: (nextTab: number, event?: React.SyntheticEvent) => void;
}

interface TableTabOption {
  key: string;
  index: number;
  label: string;
  icon?: React.ReactNode;
  isActive: boolean;
  disabled: boolean;
  tabProps?: ErpDataTableTabProps;
}

const shouldShowTableTabs = (tables: TableData[]) =>
  tables.length > 1 || (tables.length === 1 && tables[0].name !== '');

const buildTableTabOptions = (tables: TableData[], activeTab: number): TableTabOption[] =>
  tables.map((table, index) => ({
    key: table.name || `${index}`,
    index,
    label: table.name || `Bảng ${index + 1}`,
    icon: table.icon,
    isActive: index === activeTab,
    disabled: Boolean(table.tabProps?.disabled),
    tabProps: table.tabProps
  }));

function TableTabsDropdownTrigger({
  activeOption,
  totalOptions
}: {
  activeOption: TableTabOption;
  totalOptions: number;
}) {
  return (
    <Button
      variant='ghost'
      size='sm'
      className={cn(
        'h-[var(--erp-compact-tab-trigger-height)] min-w-[220px] max-w-[min(360px,calc(100vw-32px))] items-center justify-between rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-left shadow-sm transition-colors',
        'hover:bg-slate-50 hover:text-slate-950 focus-visible:ring-1 focus-visible:ring-main/30'
      )}
    >
      <span className='flex min-w-0 items-center gap-2.5'>
        <span className='flex size-7 shrink-0 items-center justify-center rounded-lg border border-main/20 bg-main/5 text-main'>
          {activeOption.icon ?? <LayoutGrid className='size-3.5' />}
        </span>

        <span className='min-w-0'>
          <span className='block text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500'>
            Bảng dữ liệu
          </span>
          <span className='block truncate text-sm font-semibold text-slate-900'>{activeOption.label}</span>
        </span>
      </span>

      <span className='ml-3 flex shrink-0 items-center gap-2'>
        <span className='rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600'>
          {totalOptions}
        </span>
        <ChevronDown className='size-4 text-slate-500' />
      </span>
    </Button>
  );
}

function TableTabsDropdownItem({
  option,
  onSelect
}: {
  option: TableTabOption;
  onSelect: (option: TableTabOption, event: Event) => void;
}) {
  return (
    <DropdownMenuItem
      disabled={option.disabled}
      className={cn(
        'flex min-h-[var(--erp-compact-tab-item-height)] items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
        option.isActive && 'text-main focus:text-main'
      )}
      onSelect={event => onSelect(option, event)}
    >
      <span
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-lg border text-slate-500',
          option.isActive ? 'border-main/20 bg-white text-main' : 'border-slate-200 bg-slate-50'
        )}
      >
        {option.icon ?? <LayoutGrid className='size-4' />}
      </span>

      <span className='min-w-0 flex-1'>
        <span className='block truncate font-medium'>{option.label}</span>
        <span className='block text-[11px] text-slate-500'>
          {option.isActive ? 'Đang hiển thị' : 'Chuyển sang bảng này'}
        </span>
      </span>

      <span className='flex w-5 shrink-0 justify-end'>
        {option.isActive && <Lineicons aria-hidden='true' icon={CheckStroke} className='size-4 text-main' />}
      </span>
    </DropdownMenuItem>
  );
}

function CompactTabsDropdown({
  options,
  activeOption,
  onChange
}: {
  options: TableTabOption[];
  activeOption: TableTabOption;
  onChange: CompactTableTabsProps['onChange'];
}) {
  return (
    <div className='flex items-center px-2 py-1.5'>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <TableTabsDropdownTrigger activeOption={activeOption} totalOptions={options.length} />
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align='start'
          sideOffset={8}
          className='w-[min(320px,calc(100vw-24px))] rounded-xl border border-slate-200 bg-white p-1.5 shadow-[0_18px_40px_rgba(15,23,42,0.14)]'
        >
          <DropdownMenuLabel className='px-3 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500'>
            Danh sách bảng
          </DropdownMenuLabel>
          <DropdownMenuSeparator className={cn('mx-1 my-1', TABLE_THEME.toolbarDivider)} />

          <div className='max-h-[320px] overflow-y-auto'>
            {options.map(option => (
              <TableTabsDropdownItem
                key={option.key}
                option={option}
                onSelect={(selectedOption, event) => {
                  if (selectedOption.disabled) {
                    event.preventDefault();
                    return;
                  }

                  onChange(selectedOption.index);
                }}
              />
            ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

const inlineTabTriggerClassName =
  'relative inline-flex h-8 items-center justify-center rounded-none border-0 bg-transparent px-4 py-0 text-[12px] font-semibold leading-none text-slate-500 shadow-none ring-0 transition-colors after:absolute after:bottom-0 after:left-0 after:h-[3px] after:w-full after:bg-main after:opacity-0 hover:bg-transparent hover:text-main hover:after:opacity-100 focus-visible:ring-0 data-[state=active]:bg-main/10 data-[state=active]:text-main data-[state=active]:shadow-none data-[state=active]:after:opacity-100';

function InlineTableTabs({
  options,
  activeTab,
  onChange
}: {
  options: TableTabOption[];
  activeTab: number;
  onChange: CompactTableTabsProps['onChange'];
}) {
  return (
    <Tabs value={String(activeTab)} className='h-8 shrink-0'>
      <TabsList className='h-8 justify-start rounded-none border-0 bg-muted p-0 text-slate-600'>
        {options.map(option => {
          const {
            className: tabClassName,
            onClick: tabOnClick,
            disabled: _disabled,
            ...triggerProps
          } = option.tabProps ?? {};

          return (
            <TabsTrigger
              key={option.key}
              {...triggerProps}
              value={String(option.index)}
              disabled={option.disabled}
              className={cn(inlineTabTriggerClassName, tabClassName)}
              onClick={event => {
                tabOnClick?.(event);

                if (!event.defaultPrevented) {
                  onChange(option.index, event);
                }
              }}
            >
              <span className='flex min-w-0 items-center justify-center gap-2'>
                {option.icon}
                <span className='inline-block translate-y-px truncate align-middle leading-none'>{option.label}</span>
              </span>
            </TabsTrigger>
          );
        })}
      </TabsList>
    </Tabs>
  );
}

export function CompactTableTabs({ tables, activeTab, onChange }: CompactTableTabsProps) {
  const { isCompact } = useResponsiveLayout();
  const options = useMemo(() => buildTableTabOptions(tables, activeTab), [activeTab, tables]);
  const activeOption = options.find(option => option.isActive) ?? options[0];

  if (!shouldShowTableTabs(tables) || !activeOption) {
    return null;
  }

  if (isCompact) {
    return <CompactTabsDropdown options={options} activeOption={activeOption} onChange={onChange} />;
  }

  return <InlineTableTabs options={options} activeTab={activeTab} onChange={onChange} />;
}
